import { useCallback, useState } from "react";
import { accessApi } from "@services/accessApi";
import { errorMessage } from "@hooks/monitoring/useApiResource";
import { ApiError } from "@models/error";
import type { CataloguePlan, PlanCreate } from "@models/access";

export interface PlanError {
  code: string | null;
  message: string;
  /** Extra data core sends with the error, e.g. the roles still holding a key. */
  detail?: Record<string, unknown>;
}

const toPlanError = (error: unknown): PlanError =>
  error instanceof ApiError
    ? { code: error.errorCode, message: error.getUserMessage(), detail: error.detail }
    : { code: null, message: errorMessage(error) };

/**
 * One permission catalogue plan (§7): preview it, then run its steps one per
 * request, in order. The plan returned by each call replaces the local copy,
 * so the stepper always shows what core recorded.
 */
export const useCataloguePlan = (initial: CataloguePlan | null = null) => {
  const [plan, setPlan] = useState<CataloguePlan | null>(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<PlanError | null>(null);

  const call = useCallback(async (run: () => Promise<CataloguePlan>): Promise<CataloguePlan | null> => {
    setBusy(true);
    setError(null);
    try {
      const next = await run();
      setPlan(next);
      return next;
    } catch (err) {
      setError(toPlanError(err));
      return null;
    } finally {
      setBusy(false);
    }
  }, []);

  const preview = useCallback((body: PlanCreate) => call(() => accessApi().createPlan(body)), [call]);

  const runStep = useCallback(
    (step: number, confirmDeploy = false) => {
      if (!plan) return Promise.resolve(null);
      return call(() => accessApi().runStep(plan.id, step, confirmDeploy ? { confirmDeploy } : {}));
    },
    [call, plan],
  );

  const cancel = useCallback(() => {
    if (!plan) return Promise.resolve(null);
    return call(() => accessApi().cancelPlan(plan.id));
  }, [call, plan]);

  const reset = useCallback(() => {
    setPlan(null);
    setError(null);
  }, []);

  return { plan, setPlan, busy, error, preview, runStep, cancel, reset };
};
